import React from "react";
import { Form } from "antd";
import RelatedFieldSelect from "./RelatedFieldSelect";
import { Identifiable, RecordFieldSchema, RecordModel, RecordsetFilters } from "../../types/core";

interface DependentFieldSelectProps<T> {
  fieldName: string | string[];
  fieldDefinition: RecordFieldSchema;
  formRef?: any;
  recordModel: RecordModel<T>;
  recordsetFilters?: RecordsetFilters;
}

const extractNestedValue = (obj: any, path: string[]): any => {
  return path.reduce((current, key) => current?.[key], obj);
};

function DependentFieldSelect<RecordType extends Identifiable>(props: DependentFieldSelectProps<RecordType>): JSX.Element {
  const { recordModel } = props;
  const leafFieldName = typeof props.fieldName === 'string' ? props.fieldName : props.fieldName[props.fieldName.length - 1];

  // Get dependent field configuration
  const dependentConfig = recordModel.dependentFields?.[leafFieldName];

  // Watch the dependency field and auxiliary field using Form.useWatch
  const dependencyValue = Form.useWatch(dependentConfig?.dependsOn || '');
  const auxValue = Form.useWatch(dependentConfig ? `${dependentConfig.dependsOn}_aux` : '');

  // Helper function to get filter value
  const getFilterValue = () => {
    if (!dependentConfig) return undefined;

    let filterValue;

    // Check if we can get the value from auxiliary data first
    if (dependentConfig.valuePath.length >= 2 && auxValue) {
      // Extract from auxiliary data, skipping the first element since it's the field name
      const auxPath = dependentConfig.valuePath.slice(1);
      filterValue = extractNestedValue(auxValue, auxPath);
    }

    // Fallback to original logic if auxiliary data not available
    if (filterValue === undefined && dependencyValue) {
      filterValue = extractNestedValue({ [dependentConfig.dependsOn]: dependencyValue }, dependentConfig.valuePath);
    }

    return filterValue;
  };

  // Calculate dynamic filters based on watched values
  const filterValue = getFilterValue();

  const dynamicFilters: RecordsetFilters = filterValue !== undefined && filterValue !== null
    ? { [dependentConfig!.filterName]: filterValue }
    : {};

  // Merge dynamic filters with existing recordsetFilters
  const mergedFilters: RecordsetFilters = {
    ...props.recordsetFilters,
    ...dynamicFilters
  };

  return (
    <RelatedFieldSelect
      {...props}
      recordsetFilters={mergedFilters}
    />
  );
}

export default DependentFieldSelect;
